/**
 * <color-orb-select> — wraps a native <select> with a custom dropdown UI that
 * renders a colour orb next to each option label. The native <select> remains
 * the source of truth for form state and is what variant-picker.js listens to.
 */

/** @type {Array<{name: string, color: string, members: string[], contains?: string[]}>} */
const COLOR_GROUPS = [
  { name: 'Zwart',      color: '#1c1c1c',  members: ['Zwart', 'Mat Zwart', 'Nacht Zwart', 'Antraciet', 'Grafiet', 'Carbon', 'Zwart Mat'] },
  { name: 'Wit',        color: '#f2f2f2',  members: ['Wit', 'Ivoor', 'Crème', 'Off-White', 'Parel', 'Wit/Crème'] },
  { name: 'Grijs',      color: '#8a8a8a',  members: ['Grijs', 'Lichtgrijs', 'Donkergrijs', 'Zilvergrijs', 'Titaniumgrijs', 'Space Grey', 'Space Gray', 'Grijs Mix'] },
  { name: 'Zilver',     color: 'linear-gradient(145deg, #f0f0f0 0%, #b8b8b8 30%, #ffffff 50%, #989898 70%, #d4d4d4 100%)',  members: ['Zilver', 'Chrome', 'Platina', 'Zilver/Grijs', 'Geborsteld Zilver'] },
  { name: 'Goud',       color: 'linear-gradient(145deg, #f5e070 0%, #c09030 30%, #ffe878 50%, #a07010 70%, #d4aa38 100%)',  members: ['Goud', 'Geelgoud', 'Goud Mat', 'Goud/Zwart'] },
  { name: 'Rosé Goud',  color: 'linear-gradient(145deg, #f8c8b4 0%, #d48878 30%, #fcd8c4 50%, #b86858 70%, #e8a890 100%)',  members: ['Rosé Goud', 'Rosegoud', 'Roségoud', 'Rose Gold', 'Rosé', 'Rosegold', 'Roze Goud'] },
  { name: 'Champagne Goud', color: 'linear-gradient(145deg, #f5e8c8 0%, #d4b878 30%, #fcf0d4 50%, #b89858 70%, #e8d4a8 100%)',  members: ['Champagne Goud', 'Champagnegoud', 'Champagne Gold'] },
  { name: 'Blauw',      color: '#2563eb',  members: ['Blauw', 'Blauw Mix', 'Lichtblauw', 'Donkerblauw', 'Navy', 'Marineblauw', 'Kobaltblauw', 'Koningsblauw', 'IJsblauw', 'Middernachtblauw', 'Grijsblauw', 'Indigo Blauw'] },
  { name: 'Rood',       color: '#dc2626',  members: ['Rood', 'Donkerrood', 'Bordeaux', 'Wijnrood', 'Robijn', 'Scarlet', 'Rood Mix'] },
  { name: 'Roze',       color: '#f472b6',  members: ['Roze', 'Lichtroze', 'Donkerroze', 'Fuchsia', 'Magenta', 'Roze Mix', 'Flamingo', 'Oud Roze'] },
  { name: 'Paars',      color: '#7c3aed',  members: ['Paars', 'Violet', 'Lila', 'Lavendel', 'Aubergine', 'Pruim'] },
  { name: 'Groen',      color: '#16a34a',  members: ['Groen', 'Lichtgroen', 'Donkergroen', 'Olijfgroen', 'Khaki', 'Mint', 'Jade', 'Groen Mix', 'Bosgroen', 'Saliegroen', 'Limoengroen', 'Fluoriserend', 'Groenblauw', 'Legergroen', 'Dennengroen'] },
  { name: 'Geel',       color: '#eab308',  members: ['Geel', 'Lichtgeel', 'Citroengeel', 'Okergeel', 'Mosterd', 'Geel Mix'] },
  { name: 'Oranje',     color: '#f97316',  members: ['Oranje', 'Lichtoranje', 'Donkeroranje', 'Terracotta', 'Zalm', 'Perzik', 'Koraal'] },
  { name: 'Bruin',      color: '#92400e',  members: ['Bruin', 'Lichtbruin', 'Donkerbruin', 'Taupe', 'Camel', 'Cognac', 'Tan', 'Chocolade', 'Kastanje', 'Mahonie', 'Walnoot', 'Mokka', 'Koffie', 'Bruin Mix'] },
  { name: 'Beige',      color: '#d4b896',  members: ['Beige', 'Zand', 'Champagne', 'Nude', 'Licht Beige', 'Crème Beige', 'Zandkleurig'] },
  { name: 'Multicolor', color: 'conic-gradient(red, yellow, lime, cyan, blue, magenta, red)', members: ['Multicolor', 'Multi', 'Regenboog', 'Gekleurd', 'Kleurrijk', 'Gemêleerd', 'Blauw/Oranje'] },
  { name: 'Transparant',color: 'linear-gradient(135deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%), linear-gradient(135deg, #ccc 25%, white 25%, white 75%, #ccc 75%)', members: ['Transparant', 'Doorzichtig', 'Clear'] },
  { name: 'Starlight',  color: '#e8e0d4',  members: ['Starlight', 'Sterrenlicht'] },
  { name: 'Print',      color: 'radial-gradient(circle at 28% 32%, #6b4c1a 14%, transparent 14%), radial-gradient(circle at 68% 28%, #6b4c1a 12%, transparent 12%), radial-gradient(circle at 48% 68%, #6b4c1a 16%, transparent 16%), radial-gradient(circle at 20% 70%, #6b4c1a 10%, transparent 10%), radial-gradient(circle at 80% 65%, #6b4c1a 13%, transparent 13%), #c8903c', members: [], contains: ['leopard', 'luipaard', 'tijger', 'tiger', 'zebra', 'panter', 'panther', 'snake print', 'slangenprint'] },
];

/** @type {Map<string, string>} */
const colorLookup = new Map();
COLOR_GROUPS.forEach((g) => g.members.forEach((m) => colorLookup.set(m.toLowerCase().trim(), g.color)));

/**
 * @param {string} name
 * @returns {string | null}
 */
function resolveOrbColor(name) {
  const k = name.toLowerCase().trim();
  const direct = colorLookup.get(k);
  if (direct) return direct;
  for (const g of COLOR_GROUPS) {
    if (g.contains && g.contains.some((c) => c && k.includes(c))) return g.color;
  }
  return null;
}

class ColorOrbSelect extends HTMLElement {
  connectedCallback() {
    /** @type {HTMLSelectElement | null} */
    const select = this.querySelector('select');
    if (!select) return;
    this._select = select;

    // Hide native select visually but keep accessible for screen readers via the orb listbox below.
    select.classList.add('color-orb-select__native');

    this._buildUI();
    this._bindEvents();

    // Variant-picker.js uses morph() to re-render the picker on every change, which strips
    // our JS-appended trigger/listbox. Rebuild when that happens.
    this._mutationObserver = new MutationObserver(() => {
      if (!this.contains(this._trigger) || !this.contains(this._listbox)) {
        this._rebuild();
      }
    });
    this._mutationObserver.observe(this, { childList: true });
  }

  disconnectedCallback() {
    this._mutationObserver?.disconnect();
    document.removeEventListener('click', this._docClick);
  }

  _rebuild() {
    const select = this.querySelector('select');
    if (!select) return;
    this._select = select;
    select.classList.add('color-orb-select__native');
    if (this._trigger?.parentNode === this) this.removeChild(this._trigger);
    if (this._listbox?.parentNode === this) this.removeChild(this._listbox);
    this._buildUI();
    this._bindEvents();
  }

  _buildUI() {
    const select = this._select;
    const selected = select.options[select.selectedIndex];
    const selectedLabel = (selected?.textContent || '').trim();
    const selectedValue = selected?.value || '';
    const selectedColor = resolveOrbColor(selectedValue);

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'color-orb-select__trigger';
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.innerHTML = `
      <span class="color-orb-select__orb" ${selectedColor ? `style="--orb-bg: ${selectedColor}"` : 'data-no-orb="true"'}></span>
      <span class="color-orb-select__label">${this._escape(selectedLabel)}</span>
      <svg class="color-orb-select__caret" aria-hidden="true" focusable="false" viewBox="0 0 10 6"><path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
    `;
    this._trigger = trigger;

    const listbox = document.createElement('ul');
    listbox.className = 'color-orb-select__listbox';
    listbox.setAttribute('role', 'listbox');
    listbox.hidden = true;
    this._listbox = listbox;

    /** @type {HTMLLIElement[]} */
    this._optionEls = [];

    Array.from(select.options).forEach((opt) => {
      const label = (opt.textContent || '').trim();
      const value = opt.value;
      const orb = resolveOrbColor(value);
      const li = document.createElement('li');
      li.className = 'color-orb-select__option';
      li.setAttribute('role', 'option');
      li.setAttribute('data-value', value);
      li.setAttribute('aria-selected', String(opt.selected));
      if (opt.disabled) li.setAttribute('aria-disabled', 'true');
      li.innerHTML = `
        <span class="color-orb-select__orb" ${orb ? `style="--orb-bg: ${orb}"` : 'data-no-orb="true"'}></span>
        <span class="color-orb-select__label">${this._escape(label)}</span>
      `;
      listbox.appendChild(li);
      this._optionEls.push(li);
    });

    this.appendChild(trigger);
    this.appendChild(listbox);
  }

  _bindEvents() {
    this._trigger.addEventListener('click', () => this._toggle());
    this._trigger.addEventListener('keydown', (e) => this._onTriggerKey(e));

    this._optionEls.forEach((li, idx) => {
      li.addEventListener('click', () => this._selectIndex(idx));
      li.addEventListener('mouseenter', () => this._setActive(idx));
    });

    this._listbox.addEventListener('keydown', (e) => this._onListKey(e));
    this._docClick = (e) => {
      if (!this.contains(/** @type {Node} */ (e.target))) this._close();
    };

    // External changes (e.g. variant-picker re-render) — re-sync trigger label/orb.
    this._select.addEventListener('change', () => this._syncFromSelect());
  }

  _toggle() {
    if (this._listbox.hidden) this._open();
    else this._close();
  }

  _open() {
    this._listbox.hidden = false;
    this._trigger.setAttribute('aria-expanded', 'true');
    document.addEventListener('click', this._docClick);
    const idx = Math.max(0, this._select.selectedIndex);
    this._setActive(idx);
    this._optionEls[idx]?.focus();
  }

  _close() {
    this._listbox.hidden = true;
    this._trigger.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', this._docClick);
  }

  /** @param {number} idx */
  _setActive(idx) {
    this._optionEls.forEach((el, i) => el.classList.toggle('color-orb-select__option--active', i === idx));
    const target = this._optionEls[idx];
    if (target) {
      target.setAttribute('tabindex', '0');
      target.focus({ preventScroll: true });
      this._optionEls.forEach((el, i) => { if (i !== idx) el.removeAttribute('tabindex'); });
    }
  }

  /** @param {number} idx */
  _selectIndex(idx) {
    const opt = this._select.options[idx];
    if (!opt || opt.disabled) return;
    if (this._select.selectedIndex !== idx) {
      this._select.selectedIndex = idx;
      this._select.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this._syncFromSelect();
    this._close();
    this._trigger.focus();
  }

  _syncFromSelect() {
    const opt = this._select.options[this._select.selectedIndex];
    if (!opt) return;
    const label = (opt.textContent || '').trim();
    const orb = resolveOrbColor(opt.value);
    const labelEl = this._trigger.querySelector('.color-orb-select__label');
    const orbEl = this._trigger.querySelector('.color-orb-select__orb');
    if (labelEl) labelEl.textContent = label;
    if (orbEl instanceof HTMLElement) {
      if (orb) {
        orbEl.style.setProperty('--orb-bg', orb);
        orbEl.removeAttribute('data-no-orb');
      } else {
        orbEl.style.removeProperty('--orb-bg');
        orbEl.setAttribute('data-no-orb', 'true');
      }
    }
    this._optionEls.forEach((el, i) => el.setAttribute('aria-selected', String(i === this._select.selectedIndex)));
  }

  /** @param {KeyboardEvent} e */
  _onTriggerKey(e) {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this._open();
    }
  }

  /** @param {KeyboardEvent} e */
  _onListKey(e) {
    const current = this._optionEls.findIndex((el) => el.classList.contains('color-orb-select__option--active'));
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this._setActive(Math.min(this._optionEls.length - 1, (current < 0 ? 0 : current + 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this._setActive(Math.max(0, (current < 0 ? 0 : current - 1)));
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (current >= 0) this._selectIndex(current);
    } else if (e.key === 'Escape' || e.key === 'Tab') {
      this._close();
      this._trigger.focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      this._setActive(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      this._setActive(this._optionEls.length - 1);
    }
  }

  /** @param {string} s */
  _escape(s) {
    return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] || c);
  }
}

if (!customElements.get('color-orb-select')) {
  customElements.define('color-orb-select', ColorOrbSelect);
}
